import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DENTIST_APPROVAL_HOLD, type Attachment, type Claim, type Mode, type Procedure } from '../types';
import {
  billedOf,
  CLAIMS_USER,
  fileByOf,
  formatDateFull,
  formatMoney,
  hasUnresolvedDecision,
  PMS,
  todayIso,
} from '../utils';
import { hasMoneyPermission, MONEY_APPROVAL_NOTE } from '@/features/shared/permissions';
import { CDT_BY_CODE, CDT_LIST } from '../feeSchedule';
import { approveFinding, askDentist, decide, redoChange, saveClaim, undoChange, clearDecision } from '../store';
import { isOutOfNetwork, useNetworksVersion } from '@/features/networks/store';
import { Spinner, Tick } from './common';
import { AttachmentViewer, EvidenceLink } from './popovers';

export type LearnEdit =
  | { kind: 'undo'; changeTitle: string }
  | { kind: 'attachment'; name: string }
  | { kind: 'narrative' }
  | { kind: 'procedure'; code: string; field: string };

export interface DetailHandle {
  isDirty: () => boolean;
  save: () => Promise<void>;
}

interface DetailProps {
  claim: Claim;
  mode: Mode;
  busy: boolean;
  confirming: boolean;
  writebackFailed: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onToast: (msg: string) => void;
  onConfirm: (learn: LearnEdit | null) => void;
  onOpenHold: (prefill?: { reason?: string; note?: string }, decisionOptionId?: string) => void;
  onTakeOffHold: () => void;
  onDontBill: () => void;
  onBack?: () => void;
}

const USER = CLAIMS_USER.name;
const QUADRANTS = ['UR', 'UL', 'LR', 'LL'];

// ---------- tooth chart (336×66, upper 1–16 / lower 32→17; rcm-02 §3) ----------
const UPPER_TEETH = Array.from({ length: 16 }, (_, i) => i + 1);
const LOWER_TEETH = Array.from({ length: 16 }, (_, i) => 32 - i);
const TOOTH_PITCH = 21;
const TOOTH_W = 16;
const TOOTH_H = 20;
const TOOTH_START_X = 4;

function teethOf(procedures: Procedure[]): Set<string> {
  const set = new Set<string>();
  for (const p of procedures) {
    const m = p.tooth ? /^#(\d{1,2})$/.exec(p.tooth) : null;
    if (m?.[1]) set.add(m[1]);
  }
  return set;
}

function ToothRow({ nums, marked, y, labelY }: { nums: number[]; marked: Set<string>; y: number; labelY: number }) {
  return (
    <>
      {nums.map((n, i) => {
        const on = marked.has(String(n));
        const x = TOOTH_START_X + i * TOOTH_PITCH;
        return (
          <g key={n}>
            <rect
              x={x}
              y={y}
              width={TOOTH_W}
              height={TOOTH_H}
              rx={5}
              fill={on ? '#0e7c78' : 'none'}
              stroke="#b8c2c0"
              strokeWidth={1.2}
            />
            <text
              x={x + TOOTH_W / 2}
              y={labelY}
              textAnchor="middle"
              className="clm-tooth-num"
              fill={on ? '#0b6663' : '#647174'}
              fontWeight={on ? 600 : 400}
            >
              {n}
            </text>
          </g>
        );
      })}
    </>
  );
}

/** ToothChart — only the claim's teeth are marked. Hidden when the claim has no teeth (e.g. cleanings). */
function ToothChart({ teeth }: { teeth: Set<string> }) {
  if (teeth.size === 0) return null;
  const label = `Tooth chart, tooth ${[...teeth].sort((a, b) => Number(a) - Number(b)).join(', tooth ')} marked`;
  return (
    <svg className="clm-toothchart" viewBox="0 0 336 66" width={336} height={66} role="img" aria-label={label}>
      <ToothRow nums={UPPER_TEETH} marked={teeth} y={12} labelY={8} />
      <ToothRow nums={LOWER_TEETH} marked={teeth} y={34} labelY={62} />
    </svg>
  );
}

function renderTooth(tooth: string | null): string {
  if (!tooth) return '—';
  if (QUADRANTS.includes(tooth.toUpperCase())) return `${tooth.toUpperCase()} quadrant`;
  return tooth;
}

function validateTooth(raw: string): { ok: true; value: string | null } | { ok: false } {
  const t = raw.trim();
  if (t === '') return { ok: true, value: null };
  if (/^(?:[1-9]|[12]\d|3[0-2])$/.test(t)) return { ok: true, value: t.startsWith('#') ? t : `#${t}` };
  if (/^#(?:[1-9]|[12]\d|3[0-2])$/.test(t)) return { ok: true, value: t };
  if (/^[A-Ta-t]$/.test(t)) return { ok: true, value: t.toUpperCase() };
  if (/^(UR|UL|LR|LL)$/i.test(t)) return { ok: true, value: t.toUpperCase() };
  return { ok: false };
}

interface Draft {
  procedures: Procedure[];
  narrativeText: string;
  remarks: string;
  attachments: Attachment[];
}

function freshDraft(claim: Claim): Draft {
  return {
    procedures: claim.procedures.map((p) => ({ ...p })),
    narrativeText: claim.narrative?.text ?? '',
    remarks: claim.remarks,
    attachments: claim.attachments.map((a) => ({ ...a })),
  };
}

export const Detail = forwardRef<DetailHandle, DetailProps>(function Detail(props, ref) {
  const { claim, mode } = props;
  const [draft, setDraft] = useState<Draft>(() => freshDraft(claim));
  const [dirty, setDirty] = useState(false);
  const [narrState, setNarrState] = useState(claim.narrative?.state ?? 'elva');
  const [narrEdited, setNarrEdited] = useState(false);
  const [procEdit, setProcEdit] = useState<{ code: string; field: string } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewer, setViewer] = useState<Attachment | null>(null);
  const [saving, setSaving] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Re-sync from the (mutated) store claim when its identity changes.
  const claimId = claim.id;
  useEffect(() => {
    setDraft(freshDraft(claim));
    setDirty(false);
    setNarrState(claim.narrative?.state ?? 'elva');
    setNarrEdited(false);
    setEditingId(null);
    setProcEdit(null);
    headingRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  const markDirty = () => {
    if (!dirty) {
      setDirty(true);
      props.onDirtyChange(true);
    }
  };

  const resync = () => {
    setDraft(freshDraft(claim));
    setDirty(false);
    props.onDirtyChange(false);
    setNarrState(claim.narrative?.state ?? 'elva');
  };

  const billed = useMemo(() => billedOf({ procedures: draft.procedures }), [draft.procedures]);

  const doSave = async (showToast = false) => {
    setSaving(true);
    await saveClaim(claim.id, {
      procedures: draft.procedures,
      narrativeText: draft.narrativeText,
      remarks: draft.remarks,
      attachments: draft.attachments,
    });
    setSaving(false);
    setDirty(false);
    props.onDirtyChange(false);
    if (showToast) props.onToast('Changes saved. The claim is still waiting for you.');
  };

  useImperativeHandle(ref, () => ({
    isDirty: () => dirty,
    save: async () => {
      await doSave();
    },
  }));

  const learnableEdit = (): LearnEdit | null => {
    const undone = claim.changes.find((c) => !c.applied);
    if (undone) return { kind: 'undo', changeTitle: undone.title };
    const added = draft.attachments.find((a) => a.userAdded);
    if (added) return { kind: 'attachment', name: added.name };
    if (narrEdited) return { kind: 'narrative' };
    if (procEdit) return { kind: 'procedure', code: procEdit.code, field: procEdit.field };
    return null;
  };

  const unresolved = hasUnresolvedDecision(claim);
  const confirmDisabled = props.busy || unresolved || editingId !== null;
  const confirmTitle = unresolved
    ? 'Answer the question above first.'
    : editingId !== null
    ? 'Save or cancel the procedure you’re editing.'
    : undefined;

  const handleConfirm = async () => {
    if (dirty) await doSave();
    props.onConfirm(learnableEdit());
  };

  // ---- attachments ----
  const onAddAttachment = (file: File) => {
    const kind: Attachment['kind'] = file.type.startsWith('image/') ? 'photo' : 'document';
    const att: Attachment = {
      id: `att-user-${String(Date.now())}`,
      name: file.name,
      kind,
      date: formatDateFull(todayIso(mode)),
      origin: `added by ${USER}`,
      objectUrl: URL.createObjectURL(file),
      userAdded: true,
    };
    setDraft((d) => ({ ...d, attachments: [...d.attachments, att] }));
    markDirty();
  };
  const removeAttachment = (id: string) => {
    setDraft((d) => ({ ...d, attachments: d.attachments.filter((a) => a.id !== id) }));
    markDirty();
  };

  // ---- changes undo/redo ----
  const onUndo = async (changeId: string) => {
    await undoChange(claim.id, changeId);
    resync();
  };
  const onRedo = async (changeId: string) => {
    await redoChange(claim.id, changeId);
    resync();
  };

  // ---- decision ----
  const onDecide = async (optionId: string) => {
    const option = claim.decision?.options.find((o) => o.id === optionId);
    if (!option) return;
    if (option.effect === 'dont_bill') {
      // Money action (D245): the option is hidden without the permission; this opens the same dialog as the action bar.
      props.onDontBill();
      return;
    }
    if (option.effect === 'hold') {
      props.onOpenHold(
        { ...(option.holdReason ? { reason: option.holdReason } : {}), ...(option.holdNote ? { note: option.holdNote } : {}) },
        optionId,
      );
      return;
    }
    await decide(claim.id, optionId);
    resync();
  };
  const onChangeDecision = async () => {
    await clearDecision(claim.id);
    resync();
  };

  // ---- narrative ----
  const narrativeUndoWarning = useMemo(() => {
    if (draft.narrativeText) return null;
    const ch = claim.changes.find((c) => !c.applied && c.undoWarning && /narrative/i.test(c.undoWarning));
    return ch?.undoWarning ?? null;
  }, [draft.narrativeText, claim.changes]);

  const onRewrite = async () => {
    setRewriting(true);
    await new Promise((r) => setTimeout(r, 800));
    const alt = claim.narrative?.alt ?? claim.narrative?.text ?? '';
    setDraft((d) => ({ ...d, narrativeText: alt }));
    setNarrState('rewritten');
    setNarrEdited(false);
    setRewriting(false);
    markDirty();
  };
  const [rewriteConfirm, setRewriteConfirm] = useState(false);

  const origin = () => {
    if (!draft.narrativeText) return 'No narrative.';
    if (narrState === 'rewritten') return 'Rewritten by ELVA just now.';
    if (narrState === 'edited' || narrEdited) return `Edited by ${USER} · ELVA’s draft is kept in the history.`;
    return `Written by ELVA from ${claim.narrative?.basis ?? 'the clinical note'}.`;
  };

  const payerShort = claim.payer.short;
  // D197: out of network with the payer and no assignment of benefits on file → the payer pays the patient.
  useNetworksVersion();
  const paysPatient = claim.assignmentOfBenefits === 'none' && isOutOfNetwork(claim.provider, claim.payer.short);
  const dentistShort = `Dr. ${claim.provider.split(' ').slice(-1)[0] ?? claim.provider}`;
  const createdDay = claim.source.kind === 'pms_draft' ? claim.source.createdAt.split(',')[0] : '';
  const reviewedDay =
    claim.source.kind === 'pms_draft'
      ? claim.source.reviewedAt.split(',')[0]
      : claim.source.reviewedAt;
  const sameDay = createdDay === reviewedDay;

  const held = claim.held;
  const approval = claim.findingApproval;
  const pendingApproval = !!approval && approval.status !== 'approved';
  const fileBy = fileByOf(claim, mode);
  // Don't bill is a money action (D245): hidden without the permission; the note stands in once.
  const canBillMoney = hasMoneyPermission();
  const dontBillButton = canBillMoney ? (
    <button type="button" className="clm-btn clm-btn-ghost" onClick={props.onDontBill}>
      Don’t bill
    </button>
  ) : (
    <span className="clm-actionbar-note">{MONEY_APPROVAL_NOTE}</span>
  );
  const teethOnClaim = useMemo(() => teethOf(draft.procedures), [draft.procedures]);

  return (
    <main className="clm-detail">
      {props.onBack ? (
        <button type="button" className="clm-back" onClick={props.onBack}>
          ← All claims
        </button>
      ) : null}
      <div className="clm-detail-inner">
        {/* write-back failure banner */}
        {props.writebackFailed ? (
          <div className="clm-banner clm-banner-danger" role="alert">
            <span className="clm-banner-title">ELVA couldn’t update {PMS} — nothing was submitted.</span>
            <span>Your changes are saved here. Try again in a moment.</span>
            <div>
              <button type="button" className="clm-btn clm-btn-sm" onClick={() => { void handleConfirm(); }}>
                Try again
              </button>
            </div>
          </div>
        ) : null}

        {/* needs the dentist's approval (D176): ELVA added a finding the chart doesn't have */}
        {approval ? (
          <FindingApprovalCard
            claim={claim}
            onToast={props.onToast}
          />
        ) : null}

        {/* held banner */}
        {held && !approval ? (
          <div className="clm-banner clm-banner-neutral">
            <span className="clm-eyebrow">{held.until ? `On hold · until ${formatDateFull(held.until)}` : 'On hold'}</span>
            <span>
              {held.reason}
              {held.note ? ` — ${held.note}` : ''}
            </span>
            <div>
              <button type="button" className="clm-btn clm-btn-sm" onClick={props.onTakeOffHold}>
                Take off hold
              </button>
            </div>
          </div>
        ) : null}

        {/* autopilot held panel (6.2c) */}
        {mode === 'autopilot' && claim.heldReason ? (
          <div className="clm-inner-neutral">
            <span className="clm-eyebrow">Held by ELVA</span>
            <p style={{ margin: '6px 0 0' }}>{claim.heldReason}</p>
          </div>
        ) : null}

        {/* 6.1 claim header */}
        <section className="clm-claimhead" aria-labelledby="clm-patient">
          <div>
            <div className="clm-eyebrow" style={{ marginBottom: 4 }}>
              Claim {claim.id} · service {formatDateFull(claim.serviceDate)}
            </div>
            <h2 id="clm-patient" className="clm-patient" tabIndex={-1} ref={headingRef}>
              {claim.patient}
            </h2>
            <p className="clm-chsub">
              {claim.payer.name} · member <span className="clm-member">{claim.memberId}</span> · {claim.provider}
            </p>
            {paysPatient ? (
              <p className="clm-chsub">
                Payment goes to: {claim.patient.split(' ')[0] ?? claim.patient} ({dentistShort} is out of network with {claim.payer.short} and no assignment of benefits is on file)
              </p>
            ) : null}
            {fileBy ? (
              <p className="clm-filing">
                <span className="clm-badge b-warn">{fileBy.label}</span>
                <span>
                  {payerShort}’s filing limit. ELVA sends the claim as soon as you confirm it.
                </span>
              </p>
            ) : null}
            {claim.source.kind === 'pms_draft' ? (
              <p className="clm-chsrc">
                From {PMS}’s draft, created at checkout on {createdDay}.{' '}
                {sameDay ? 'ELVA reviewed it the same day.' : `ELVA reviewed it on ${reviewedDay ?? ''}.`}
              </p>
            ) : null}
          </div>
          <div className="clm-billed-wrap">
            <div className="clm-meta">Billed</div>
            <div className="clm-billed num">{formatMoney(billed)}</div>
            {claim.usuallyPays != null ? (
              <div className="clm-meta">
                {payerShort} usually pays about {formatMoney(claim.usuallyPays)}
              </div>
            ) : null}
          </div>
        </section>

        {/* tooth chart — only the claim's teeth are marked; hidden when none (e.g. cleanings) */}
        {teethOnClaim.size > 0 ? (
          <div className="clm-toothwrap">
            <ToothChart teeth={teethOnClaim} />
          </div>
        ) : null}

        {/* 6.2a nothing to change */}
        {!claim.decision && claim.changes.length === 0 && claim.checkedAgainst ? (
          <section className="clm-inner" aria-labelledby="clm-nochange">
            <div className="clm-inner-row">
              <Tick />
              <div>
                <div className="clm-note-title" id="clm-nochange">
                  ELVA checked this claim and changed nothing
                </div>
                <p style={{ margin: '4px 0 8px' }}>{claim.checkedAgainst.sentence}</p>
                <EvidenceLink id={claim.checkedAgainst.evidenceId} label={claim.checkedAgainst.label} claim={claim} />
              </div>
            </div>
          </section>
        ) : null}

        {/* 6.2b ELVA created */}
        {claim.source.kind === 'elva_created' ? (
          <section className="clm-inner-psoft">
            <div className="clm-inner-row">
              <span className="clm-plus" aria-hidden="true">
                +
              </span>
              <div>
                <div className="clm-note-title psoft">{PMS} had no claim for this visit</div>
                <p style={{ margin: '4px 0 0' }}>{claim.source.note}</p>
              </div>
            </div>
          </section>
        ) : null}

        {/* 6.3 decision card */}
        {claim.decision ? <DecisionCard claim={claim} onDecide={(id) => { void onDecide(id); }} onChange={() => { void onChangeDecision(); }} /> : null}

        {/* 6.4 what ELVA changed */}
        {claim.changes.length > 0 ? (
          <section className="clm-card" aria-labelledby="clm-changes">
            <div className="clm-card-head">
              <h3 className="clm-h3" id="clm-changes">
                <span className="clm-sec-no">01</span>What ELVA changed · {claim.changes.length}
              </h3>
              <span className="clm-meta">Each change is already applied. Undo any you disagree with.</span>
            </div>
            {claim.changes.map((ch) => (
              <div key={ch.id} className={`clm-change${ch.applied ? '' : ' clm-change-undone'}`}>
                {ch.applied ? <Tick /> : <span style={{ width: 22, flex: '0 0 22px' }} />}
                <div className="clm-change-main">
                  <div className="clm-change-title">
                    {!ch.applied ? <span className="clm-badge b-mute" style={{ marginRight: 8 }}>Undone</span> : null}
                    {ch.title}
                  </div>
                  <p className="clm-change-why">{ch.why}</p>
                  {ch.evidenceId && ch.evidenceLabel ? (
                    <div style={{ marginTop: 6 }}>
                      <EvidenceLink id={ch.evidenceId} label={ch.evidenceLabel} claim={claim} />
                    </div>
                  ) : null}
                  {!ch.applied && ch.undoWarning ? <div className="clm-undo-warn">{ch.undoWarning}</div> : null}
                </div>
                <button
                  type="button"
                  className="clm-btn clm-btn-ghost clm-btn-sm"
                  aria-label={`${ch.applied ? 'Undo' : 'Redo'}: ${ch.title}`}
                  onClick={() => { void (ch.applied ? onUndo(ch.id) : onRedo(ch.id)); }}
                >
                  {ch.applied ? 'Undo' : 'Redo'}
                </button>
              </div>
            ))}
          </section>
        ) : null}

        {/* 6.5 procedures */}
        <ProceduresCard
          draft={draft}
          setDraft={setDraft}
          editingId={editingId}
          setEditingId={setEditingId}
          markDirty={markDirty}
          onProcEdit={(code, field) => { setProcEdit({ code, field }); }}
        />

        {/* 6.6 narrative */}
        {claim.narrative ? (
          <section className="clm-card" aria-labelledby="clm-narr">
            <div className="clm-card-head">
              <h3 className="clm-h3" id="clm-narr">
                Narrative · {claim.narrative.code}
              </h3>
              {rewriteConfirm ? (
                <div className="clm-inline-confirm">
                  Replace your edits with a new draft?
                  <button
                    type="button"
                    className="clm-btn clm-btn-sm clm-btn-primary"
                    onClick={() => {
                      setRewriteConfirm(false);
                      void onRewrite();
                    }}
                  >
                    Replace
                  </button>
                  <button type="button" className="clm-btn clm-btn-sm" onClick={() => { setRewriteConfirm(false); }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="clm-btn clm-btn-ghost clm-btn-sm"
                  disabled={rewriting}
                  onClick={() => { if (narrEdited) setRewriteConfirm(true); else void onRewrite(); }}
                >
                  {rewriting ? <Spinner label="Rewriting…" /> : 'Rewrite'}
                </button>
              )}
            </div>
            <label className="clm-visually-hidden" htmlFor="clm-narr-text">
              Clinical narrative
            </label>
            <textarea
              id="clm-narr-text"
              className="clm-textarea"
              rows={5}
              value={draft.narrativeText}
              onChange={(e) => {
                setDraft((d) => ({ ...d, narrativeText: e.target.value }));
                setNarrState('edited');
                setNarrEdited(true);
                markDirty();
              }}
            />
            <div className="clm-origin">{origin()}</div>
            {narrativeUndoWarning ? <div className="clm-undo-warn">{narrativeUndoWarning}</div> : null}
          </section>
        ) : null}

        {/* 6.7 attachments */}
        <section className="clm-card" aria-labelledby="clm-att">
          <div className="clm-card-head">
            <h3 className="clm-h3" id="clm-att">
              Attachments · {draft.attachments.length}
            </h3>
            <button type="button" className="clm-btn clm-btn-ghost clm-btn-sm" onClick={() => fileRef.current?.click()}>
              Add attachment
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onAddAttachment(f);
                e.target.value = '';
              }}
            />
          </div>
          {draft.attachments.length === 0 ? (
            <p className="clm-meta">No attachments.</p>
          ) : (
            <div className="clm-tiles">
              {draft.attachments.map((a) => (
                <div key={a.id} className="clm-tile">
                  <span className={`clm-thumb ${a.kind === 'xray' ? 'clm-thumb-xray' : 'clm-thumb-light'}`}>
                    {a.objectUrl && a.kind !== 'document' ? <img src={a.objectUrl} alt="" /> : null}
                  </span>
                  <div className="clm-tile-main">
                    <div className="clm-tile-name">{a.name}</div>
                    <div className="clm-tile-meta">
                      {a.date} · {a.origin}
                    </div>
                  </div>
                  <button type="button" className="clm-btn clm-btn-ghost clm-btn-sm" onClick={() => { setViewer(a); }}>
                    View
                  </button>
                  {a.userAdded ? (
                    <button
                      type="button"
                      className="clm-tile-remove"
                      aria-label={`Remove ${a.name}`}
                      onClick={() => { removeAttachment(a.id); }}
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 6.8 remarks */}
        <section className="clm-card" aria-labelledby="clm-remarks">
          <div className="clm-card-head" style={{ marginBottom: 8 }}>
            <h3 className="clm-h3" id="clm-remarks">
              Remarks for the payer
            </h3>
          </div>
          <label className="clm-visually-hidden" htmlFor="clm-remarks-input">
            Remarks
          </label>
          <input
            id="clm-remarks-input"
            className="clm-remarks"
            maxLength={80}
            placeholder="Optional — sent to the payer with the claim"
            value={draft.remarks}
            onChange={(e) => {
              setDraft((d) => ({ ...d, remarks: e.target.value }));
              markDirty();
            }}
          />
          {draft.remarks.length > 0 ? <div className="clm-counter">{draft.remarks.length}/80</div> : null}
        </section>

        {/* 6.9 action bar */}
        <div className="clm-actionbar">
          {held ? (
            <>
              {pendingApproval ? (
                <button
                  type="button"
                  className="clm-btn clm-btn-primary clm-btn-lg"
                  disabled
                  title={`${approval.dentist} approves the finding first.`}
                >
                  Confirm and send
                </button>
              ) : (
                <button type="button" className="clm-btn clm-btn-primary clm-btn-lg" onClick={props.onTakeOffHold}>
                  Take off hold
                </button>
              )}
              {dontBillButton}
            </>
          ) : (
            <>
              <button
                type="button"
                className="clm-btn clm-btn-primary clm-btn-lg"
                disabled={confirmDisabled}
                title={confirmTitle}
                aria-describedby={unresolved ? 'clm-confirm-hint' : undefined}
                onClick={() => { void handleConfirm(); }}
              >
                {props.confirming ? <Spinner label="Confirming…" /> : 'Confirm and send'}
              </button>
              {unresolved ? (
                <span id="clm-confirm-hint" className="clm-visually-hidden">
                  Answer the question above first.
                </span>
              ) : null}
              {dirty ? (
                <button type="button" className="clm-btn clm-btn-lg" disabled={saving} onClick={() => { void doSave(true); }}>
                  {saving ? <Spinner label="Saving…" /> : 'Save changes'}
                </button>
              ) : null}
              <button type="button" className="clm-btn clm-btn-ghost" onClick={() => { props.onOpenHold(); }}>
                Hold
              </button>
              {dontBillButton}
            </>
          )}
          <span className="clm-actionbar-meta">
            {claim.source.kind === 'elva_created'
              ? `On confirm, ELVA creates the claim in ${PMS} and submits it to ${payerShort}.`
              : `On confirm, ELVA updates the claim in ${PMS} and submits it to ${payerShort}.`}
          </span>
        </div>

        {/* 6.10 history */}
        <p className="clm-history">History: {claim.history.join(' · ')}</p>
      </div>

      {viewer ? <AttachmentViewer attachment={viewer} onClose={() => { setViewer(null); }} /> : null}
    </main>
  );
});

// ---------- decision card ----------

function DecisionCard({
  claim,
  onDecide,
  onChange,
}: {
  claim: Claim;
  onDecide: (optionId: string) => void;
  onChange: () => void;
}) {
  if (!claim.decision) return null;
  const decision = claim.decision;
  const chosen = decision.chosen;

  if (chosen) {
    const option = decision.options.find((o) => o.id === chosen);
    return (
      <section className="clm-resolved" aria-label="Decision">
        <Tick />
        <span className="clm-resolved-text">Decision: {option?.resolvedText}</span>
        <button type="button" className="clm-btn clm-btn-ghost clm-btn-sm" onClick={onChange}>
          Change
        </button>
      </section>
    );
  }

  // Money options (Don't bill) are hidden without the permission; the note shows once (D245).
  const canBillMoney = hasMoneyPermission();
  const options = decision.options.filter((o) => canBillMoney || o.effect !== 'dont_bill');
  const hiddenMoney = options.length < decision.options.length;

  const onKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? (idx + 1) % options.length : (idx - 1 + options.length) % options.length;
      const el = document.getElementById(`clm-opt-${String(next)}`);
      el?.focus();
    }
  };

  return (
    <section className="clm-decision" aria-labelledby="clm-dec-title">
      <div className="clm-decision-head">
        <span className="clm-decision-eyebrow" style={{ flexBasis: '100%' }}>
          Needs your decision
        </span>
        <h3 className="clm-decision-title" id="clm-dec-title">
          {decision.title}
        </h3>
      </div>
      <div className="clm-decision-body">
        <p className="clm-decision-expl">{decision.body}</p>
        <div className="clm-options" role="radiogroup" aria-labelledby="clm-dec-title">
          {options.map((o, i) => (
            <button
              key={o.id}
              id={`clm-opt-${String(i)}`}
              type="button"
              role="radio"
              aria-checked={false}
              className={`clm-option${o.style === 'ghost' ? ' clm-option-ghost' : ''}`}
              onClick={() => { onDecide(o.id); }}
              onKeyDown={(e) => { onKeyDown(e, i); }}
            >
              {o.label}
            </button>
          ))}
        </div>
        {hiddenMoney ? <p className="clm-meta">{MONEY_APPROVAL_NOTE}</p> : null}
        {decision.evidence.length > 0 ? (
          <div className="clm-evlinks">
            {decision.evidence.map((e) => (
              <EvidenceLink key={e.id} id={e.id} label={e.label} claim={claim} />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ---------- finding the dentist must approve (D176) ----------

/** "Dr. Marcus Lee" -> "Dr. Lee". */
function dentistShortOf(name: string): string {
  return `Dr. ${name.split(' ').slice(-1)[0] ?? name}`;
}

/**
 * The hold reason "Needs Dr. Lee's approval": ELVA added a clinical finding that isn't written
 * in the chart (e.g. read from an X-ray). The card quotes it and offers "Ask Dr. Lee"; the
 * dentist then approves on this screen by typing his name (no phone or email step in V1).
 */
function FindingApprovalCard({ claim, onToast }: { claim: Claim; onToast: (msg: string) => void }) {
  const approval = claim.findingApproval;
  const [typed, setTyped] = useState('');
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  if (!approval) return null;
  const short = dentistShortOf(approval.dentist);
  const last = (approval.dentist.split(' ').slice(-1)[0] ?? '').toLowerCase();

  if (approval.status === 'approved') {
    return (
      <section className="clm-resolved" aria-label="Dentist approval">
        <Tick />
        <span className="clm-resolved-text">
          {approval.dentist} approved the finding in the narrative{approval.approvedAt ? ` at ${approval.approvedAt}` : ''}.
        </span>
      </section>
    );
  }

  const ask = async () => {
    setWorking(true);
    await askDentist(claim.id);
    setWorking(false);
  };
  const approve = async () => {
    if (!typed.trim().toLowerCase().includes(last) || last === '') {
      setError(`Type ${approval.dentist}’s name to approve.`);
      return;
    }
    setError('');
    setWorking(true);
    await approveFinding(claim.id);
    setWorking(false);
    onToast(`${short} approved the finding. The claim is ready to confirm.`);
  };

  return (
    <section className="clm-decision" aria-labelledby="clm-appr-title">
      <div className="clm-decision-head">
        <span className="clm-decision-eyebrow" style={{ flexBasis: '100%' }}>
          On hold · {claim.held?.reason ?? DENTIST_APPROVAL_HOLD}
        </span>
        <h3 className="clm-decision-title" id="clm-appr-title">
          ELVA added a finding that isn’t in the chart
        </h3>
      </div>
      <div className="clm-decision-body">
        <p className="clm-decision-expl">
          ELVA read {approval.source} and added this to the narrative:
        </p>
        <blockquote className="clm-quote">“{approval.finding}”</blockquote>
        <p className="clm-decision-expl">
          {short}’s note for this visit doesn’t record it. A finding the chart doesn’t have goes to {claim.payer.short} only
          after the dentist approves it.
        </p>
        {approval.status === 'needed' ? (
          <div className="clm-options">
            <button type="button" className="clm-btn clm-btn-primary" disabled={working} onClick={() => { void ask(); }}>
              {working ? <Spinner label="Asking…" /> : `Ask ${short}`}
            </button>
          </div>
        ) : (
          <div className="clm-appr-sign">
            <p className="clm-decision-expl">
              Asked {short}{approval.askedAt ? ` at ${approval.askedAt}` : ''}. He approves on this screen by typing his name. Nothing
              goes to {claim.payer.short} until he does.
            </p>
            <label className="clm-label" htmlFor="clm-appr-name">
              {short} types his name to approve
            </label>
            <div className="clm-appr-row">
              <input
                id="clm-appr-name"
                className="clm-field"
                value={typed}
                placeholder={approval.dentist}
                autoComplete="off"
                onChange={(e) => { setTyped(e.target.value); setError(''); }}
              />
              <button type="button" className="clm-btn clm-btn-primary" disabled={working || !typed.trim()} onClick={() => { void approve(); }}>
                {working ? <Spinner label="Approving…" /> : 'Approve'}
              </button>
            </div>
            {error ? <div className="clm-field-err" role="alert">{error}</div> : null}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- procedures card ----------

function ProceduresCard({
  draft,
  setDraft,
  editingId,
  setEditingId,
  markDirty,
  onProcEdit,
}: {
  draft: Draft;
  setDraft: React.Dispatch<React.SetStateAction<Draft>>;
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  markDirty: () => void;
  onProcEdit: (code: string, field: string) => void;
}) {
  return (
    <section className="clm-card" aria-labelledby="clm-proc">
      <div className="clm-card-head">
        <h3 className="clm-h3" id="clm-proc">
          Procedures
        </h3>
        <button
          type="button"
          className="clm-btn clm-btn-ghost clm-btn-sm"
          disabled={editingId !== null}
          onClick={() => {
            const id = `p-new-${String(Date.now())}`;
            setDraft((d) => ({
              ...d,
              procedures: [...d.procedures, { id, code: '', description: '', tooth: null, fee: 0 }],
            }));
            setEditingId(id);
          }}
        >
          Add a procedure
        </button>
      </div>
      <div className="clm-tbl-wrap">
        <table className="clm-tbl">
          <thead>
            <tr>
              <th>Code</th>
              <th>Description</th>
              <th>Tooth</th>
              <th className="ta-right">Fee</th>
              <th className="clm-visually-hidden">Edit</th>
            </tr>
          </thead>
          <tbody>
            {draft.procedures.map((p) =>
              editingId === p.id ? (
                <ProcEditRow
                  key={p.id}
                  proc={p}
                  onlyRow={draft.procedures.length === 1}
                  onCancel={() => {
                    if (p.code === '' ) {
                      setDraft((d) => ({ ...d, procedures: d.procedures.filter((x) => x.id !== p.id) }));
                    }
                    setEditingId(null);
                  }}
                  onSave={(next) => {
                    setDraft((d) => ({ ...d, procedures: d.procedures.map((x) => (x.id === p.id ? next : x)) }));
                    setEditingId(null);
                    markDirty();
                    onProcEdit(next.code, 'code');
                  }}
                  onRemove={() => {
                    setDraft((d) => ({ ...d, procedures: d.procedures.filter((x) => x.id !== p.id) }));
                    setEditingId(null);
                    markDirty();
                  }}
                />
              ) : (
                <tr key={p.id}>
                  <td className="clm-code">{p.code || '—'}</td>
                  <td>{p.description}</td>
                  <td>
                    {renderTooth(p.tooth)}
                    {p.elvaTouched ? (
                      <span className="clm-badge b-info" style={{ marginLeft: 8, fontSize: 11 }}>
                        ELVA
                      </span>
                    ) : null}
                  </td>
                  <td className="ta-right num">{formatMoney(p.fee)}</td>
                  <td className="ta-right">
                    <button
                      type="button"
                      className="clm-btn clm-btn-ghost clm-btn-sm"
                      disabled={editingId !== null}
                      onClick={() => { setEditingId(p.id); }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      <div className="clm-tbl-foot">
        Fees come from your fee schedule. Edit the code or tooth here — ELVA updates {PMS} when you confirm.
      </div>
    </section>
  );
}

function ProcEditRow({
  proc,
  onlyRow,
  onSave,
  onCancel,
  onRemove,
}: {
  proc: Procedure;
  onlyRow: boolean;
  onSave: (p: Procedure) => void;
  onCancel: () => void;
  onRemove: () => void;
}) {
  const [code, setCode] = useState(proc.code);
  const [codeQuery, setCodeQuery] = useState(proc.code ? `${proc.code} · ${proc.description}` : '');
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState(proc.description);
  const [fee, setFee] = useState(proc.fee);
  const [toothRaw, setToothRaw] = useState(proc.tooth ?? '');
  const [toothErr, setToothErr] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const matches = useMemo(() => {
    const q = codeQuery.toLowerCase();
    return CDT_LIST.filter((e) => e.code.toLowerCase().includes(q) || e.description.toLowerCase().includes(q)).slice(0, 8);
  }, [codeQuery]);

  const pick = (c: string) => {
    const e = CDT_BY_CODE[c];
    if (!e) return;
    setCode(e.code);
    setDescription(e.description);
    setFee(e.fee);
    setCodeQuery(`${e.code} · ${e.description}`);
    setOpen(false);
  };

  const save = () => {
    const v = validateTooth(toothRaw);
    if (!v.ok) {
      setToothErr(true);
      return;
    }
    if (!code) return;
    onSave({ ...proc, code, description, fee, tooth: v.value, elvaTouched: false });
  };

  return (
    <tr>
      <td colSpan={5}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
          <div className="clm-combo" style={{ flex: '2 1 240px' }}>
            <input
              className="clm-field"
              value={codeQuery}
              placeholder="D2740 · Crown, porcelain/ceramic"
              aria-label="Code"
              onChange={(e) => {
                setCodeQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => { setOpen(true); }}
            />
            {open && matches.length > 0 ? (
              <div className="clm-combo-list" role="listbox">
                {matches.map((e) => (
                  <div
                    key={e.code}
                    role="option"
                    aria-selected={e.code === code}
                    className="clm-combo-opt"
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      pick(e.code);
                    }}
                  >
                    {e.code} · {e.description}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div style={{ flex: '1 1 120px' }}>
            <input
              className="clm-field"
              value={toothRaw}
              aria-label="Tooth"
              placeholder="Tooth"
              onChange={(e) => {
                setToothRaw(e.target.value);
                setToothErr(false);
              }}
            />
            {toothErr ? <div className="clm-field-err">Enter a tooth number (1–32 or A–T) or a quadrant.</div> : null}
          </div>
          <div style={{ flex: '0 0 110px' }}>
            <input className="clm-field" value={formatMoney(fee)} readOnly aria-label="Fee" />
            <div className="clm-field-hint">From your fee schedule</div>
          </div>
          <div className="clm-row-actions">
            <button type="button" className="clm-btn clm-btn-sm clm-btn-primary" onClick={save}>
              Save
            </button>
            <button type="button" className="clm-btn clm-btn-sm clm-btn-ghost" onClick={onCancel}>
              Cancel
            </button>
            {confirmRemove ? (
              <span className="clm-inline-confirm">
                Remove {code || 'this'} from this claim?
                <button type="button" className="clm-btn clm-btn-sm clm-btn-dangertext" onClick={onRemove}>
                  Remove
                </button>
                <button type="button" className="clm-btn clm-btn-sm" onClick={() => { setConfirmRemove(false); }}>
                  Keep
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="clm-btn clm-btn-sm clm-btn-ghost clm-btn-dangertext"
                disabled={onlyRow}
                title={onlyRow ? 'A claim needs at least one procedure. Use Don’t bill instead.' : undefined}
                onClick={() => { setConfirmRemove(true); }}
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}
